/**
 * AddEditGoalContentV2 — create/edit form for one topic (goal).
 *
 * Edit mode (default): loads the row from the goal-content list by slug, then
 * lets an admin edit the topic settings (retired / shown in onboarding / shown
 * in Settings), the welcome + free-trial-offer copy and the recommended
 * meditation — a Session OR a Quick Tap (the picker offers both, labeled by
 * type). Submits via PUT /admin/goal-content/{slug}.
 *
 * Create mode (`mode="create"`): adds a slug field (suggested from the label).
 * Submits via POST /admin/goal-content.
 *
 * The onboarding-only sections (03–07) are disabled while "Show in onboarding"
 * is off. Their values are kept (and still saved), so switching it back on
 * restores them.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';

import { fetchAllGoalContent, saveGoalContent, createGoalContent } from '../../../actions/goalContentActions';
import { fetchAllSessions } from '../../../actions/sessionActions';
import { fetchAllQuickTapSessions } from '../../../actions/quickTapsActions';
import { fetchAllMediaResources } from '../../../actions/mediaResourceActions';
import { uploadImage, clearMedia } from '../../../actions/mediaActions';
import { preventEnterFormSubmission } from '../../../utils/formUtils';

import {
  FormSection,
  FieldBlock,
  FieldRow,
  TextInput,
  Textarea,
  SearchSelect,
  ToggleSwitch,
  UploadTile,
} from '../../redesign/edit';
import LoadingBackdrop from '../../Common/LoadingBackdrop';

// The onboarding fallback: the server forbids retiring it, showing it in
// Settings, or hiding it from onboarding, so the form locks those switches.
const FALLBACK_SLUG = 'general';
const SLUG_PATTERN = /^[a-z0-9_]+$/;

// "Better Sleep!" -> "better_sleep" (the slug is only a suggestion; it stays editable).
const slugify = (label) =>
  String(label || '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 100);

const EMPTY_FORM = {
  goal_slug: '',
  is_retired: false,
  show_in_onboarding: false,
  show_in_settings: true,
  label: '',
  icon_url: '',
  icon_preview: null,
  create_account_subtitle: '',
  welcome_title: '',
  welcome_subtitle: '',
  // Two fixed stat cards: { value, label }. An empty card is dropped on save.
  welcome_stats: [{ value: '', label: '' }, { value: '', label: '' }],
  welcome_social_proof: '',
  offer_title: '',
  offer_testimonial: '',
  offer_testimonial_author: '',
  second_offer_subtitle: '',
  // Array of { text, icon_url } rows; the icon is a Media Resource URL.
  second_offer_benefits: [],
  // The recommended meditation is polymorphic (Session or Quick Tap). We hold
  // it as a single composite string `"<type>:<id>"` so one SearchSelect can
  // offer both id spaces (a Session and a Quick Tap can share a numeric id);
  // buildPayload splits it back into recommended_content_type/_id. '' = none.
  recommended_content: '',
  recommended_session_blurb: '',
  recommended_session_headline: '',
  recommended_session_prompt: '',
};

// The recommended-content types, matching the APIv2 GoalRecommendedContentType
// enum (recommended_content_type on goal_contents).
const CONTENT_TYPE_SESSION = 'session';
const CONTENT_TYPE_QUICK_TAP = 'quick-tap-session';

// `"<type>:<id>"` <-> { type, id }. The picker's option values and the form's
// stored value use this composite so the two id spaces never collide.
const encodeContent = (type, id) => (type && id != null ? `${type}:${id}` : '');
function decodeContent(composite) {
  if (!composite) return { type: null, id: null };
  const idx = String(composite).indexOf(':');
  if (idx < 0) return { type: null, id: null };
  const type = composite.slice(0, idx);
  const id = Number(composite.slice(idx + 1));
  return { type, id: Number.isFinite(id) ? id : null };
}

// Mirrors the M:SS formatting used elsewhere (e.g. globalSearch.js, DurationCell).
function formatDuration(seconds) {
  const n = Number(seconds);
  if (!Number.isFinite(n) || n <= 0) return '';
  const total = Math.round(n);
  const min = Math.floor(total / 60);
  const sec = total % 60;
  return `${min}:${sec < 10 ? '0' : ''}${sec}`;
}

// Coerce server `welcome_stats` (0–2 rows) into exactly two editable rows.
function normalizeStats(stats) {
  const rows = (Array.isArray(stats) ? stats : [])
    .map((s) => ({ value: s?.value || '', label: s?.label || '' }));
  while (rows.length < 2) rows.push({ value: '', label: '' });
  return rows.slice(0, 2);
}

// `confirmSubmit` (optional): an async check run before saving; resolve false to cancel the save.
export default function AddEditGoalContentV2({ selectedSlug, onLoaded, mode = 'edit', confirmSubmit }) {
  const isCreate = mode === 'create';
  const isFallback = !isCreate && selectedSlug === FALLBACK_SLUG;
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const goalContents = useSelector((s) => s.goalContent?.goalContents);
  const allSessions = useSelector((s) => s.sessions?.all_sessions);
  const allQuickTaps = useSelector((s) => s.quick_taps?.all_quick_tap_sessions);
  const mediaResources = useSelector((s) => s.mediaResources?.mediaResources);

  const [formData, setFormData] = useState(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);

  // Only hydrate the form once per slug, so a background list refetch can't
  // stomp in-progress edits.
  const loadedSlugRef = useRef(null);
  // Once the admin edits the slug by hand, stop overwriting it from the label.
  const slugTouchedRef = useRef(false);

  useEffect(() => {
    dispatch(fetchAllGoalContent());
  }, [dispatch]);

  useEffect(() => {
    if (!allSessions) dispatch(fetchAllSessions());
  }, [dispatch, allSessions]);

  useEffect(() => {
    if (!allQuickTaps) dispatch(fetchAllQuickTapSessions());
  }, [dispatch, allQuickTaps]);

  useEffect(() => {
    dispatch(fetchAllMediaResources());
  }, [dispatch]);

  // Media Resources as icon-picker options (value = hosted URL, with a thumbnail).
  const mediaOptions = useMemo(() => {
    if (!Array.isArray(mediaResources)) return [];
    return mediaResources
      .map((mr) => ({ value: mr.resource_url, label: mr.name || '(unnamed)', image: mr.resource_url }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [mediaResources]);

  // Sessions AND Quick Taps as SearchSelect options for the recommendation
  // picker. Each option's `value` is the composite `"<type>:<id>"` so the two
  // independent id spaces never collide. Active items only, plus the
  // currently-selected one even if it has since gone inactive (so the saved
  // choice always shows). The sublabel is prefixed with the type ("Session" /
  // "Quick Tap") so the two are distinguishable, and ends with the numeric id
  // — many items share a title, and the id is what the content team uses to
  // tell duplicates apart.
  const recommendedOptions = useMemo(() => {
    const sessionToOption = (s) => ({
      value: encodeContent(CONTENT_TYPE_SESSION, Number(s.session_id)),
      label: s.session_name || `Session ${s.session_id}`,
      sublabel: [
        'Session',
        s.subcategory_title || s.category_title,
        s.author_name,
        formatDuration(s.session_length_in_sec),
        `#${s.session_id}`,
      ].filter(Boolean).join(' · '),
      image: s.session_image || '',
    });
    // Quick Taps come from GET /quick-tap-sessions in legacy mode, whose
    // QuickTapSessionCmsListResource uses SHORT field names (id, title,
    // is_active, duration_in_sec, image) — not the DB-prefixed quick_tap_*
    // names the sessions store uses.
    const quickTapToOption = (q) => ({
      value: encodeContent(CONTENT_TYPE_QUICK_TAP, Number(q.id)),
      label: q.title || `Quick Tap ${q.id}`,
      sublabel: [
        'Quick Tap',
        q.author?.author_name,
        formatDuration(q.duration_in_sec),
        `#${q.id}`,
      ].filter(Boolean).join(' · '),
      image: q.image || '',
    });

    let opts = [
      ...(Array.isArray(allSessions) ? allSessions.filter((s) => s.session_is_active).map(sessionToOption) : []),
      ...(Array.isArray(allQuickTaps) ? allQuickTaps.filter((q) => q.is_active).map(quickTapToOption) : []),
    ];

    // Keep the saved selection visible even if that item is now inactive.
    const selected = formData.recommended_content;
    if (selected && !opts.some((o) => o.value === selected)) {
      const { type, id } = decodeContent(selected);
      if (type === CONTENT_TYPE_SESSION && Array.isArray(allSessions)) {
        const s = allSessions.find((x) => Number(x.session_id) === id);
        if (s) opts = [sessionToOption(s), ...opts];
      } else if (type === CONTENT_TYPE_QUICK_TAP && Array.isArray(allQuickTaps)) {
        const q = allQuickTaps.find((x) => Number(x.id) === id);
        if (q) opts = [quickTapToOption(q), ...opts];
      }
    }
    return opts.sort((a, b) => a.label.localeCompare(b.label));
  }, [allSessions, allQuickTaps, formData.recommended_content]);

  useEffect(() => {
    if (isCreate || !Array.isArray(goalContents) || loadedSlugRef.current === selectedSlug) return;
    const row = goalContents.find((c) => c.goal_slug === selectedSlug);
    if (!row) return;

    loadedSlugRef.current = selectedSlug;
    setFormData({
      goal_slug: row.goal_slug,
      is_retired: !!row.is_retired,
      // Rows from an API that predates the flags are onboarding-only topics.
      show_in_onboarding: row.show_in_onboarding ?? true,
      show_in_settings: row.show_in_settings ?? false,
      label: row.label || '',
      icon_url: row.icon_url || '',
      icon_preview: null,
      create_account_subtitle: row.create_account_subtitle || '',
      welcome_title: row.welcome_title || '',
      welcome_subtitle: row.welcome_subtitle || '',
      welcome_stats: normalizeStats(row.welcome_stats),
      welcome_social_proof: row.welcome_social_proof || '',
      offer_title: row.offer_title || '',
      offer_testimonial: row.offer_testimonial || '',
      offer_testimonial_author: row.offer_testimonial_author || '',
      second_offer_subtitle: row.second_offer_subtitle || '',
      second_offer_benefits: Array.isArray(row.second_offer_benefits)
        ? row.second_offer_benefits.map((b) => ({ text: b?.text || '', icon_url: b?.icon_url || null }))
        : [],
      // Prefer the polymorphic pair; fall back to the legacy session id for
      // rows/APIs that predate Quick Tap recommendations (treated as a session).
      recommended_content: row.recommended_content_type != null && row.recommended_content_id != null
        ? encodeContent(row.recommended_content_type, Number(row.recommended_content_id))
        : (row.recommended_session_id != null
          ? encodeContent(CONTENT_TYPE_SESSION, Number(row.recommended_session_id))
          : ''),
      recommended_session_blurb: row.recommended_session_blurb || '',
      recommended_session_headline: row.recommended_session_headline || '',
      recommended_session_prompt: row.recommended_session_prompt || '',
    });
    onLoaded?.({ updated_at: row.updated_at });
  }, [goalContents, selectedSlug, onLoaded, isCreate]);

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (isCreate && name === 'label' && !slugTouchedRef.current) next.goal_slug = slugify(value);
      return next;
    });
  };

  const handleSlugChange = (e) => {
    slugTouchedRef.current = true;
    setFormData((prev) => ({ ...prev, goal_slug: e.target.value.toLowerCase() }));
  };

  const handleFlagChange = (name) => (e) => {
    const { checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  // Welcome stat cards ({ value, label }) — two fixed rows.
  const updateStat = (index, field, value) =>
    setFormData((prev) => {
      const welcome_stats = prev.welcome_stats.map((s, i) => (i === index ? { ...s, [field]: value } : s));
      return { ...prev, welcome_stats };
    });

  // 2nd-offer benefit rows ({ text, icon_url }).
  const addBenefit = () =>
    setFormData((prev) => ({
      ...prev,
      second_offer_benefits: [...(prev.second_offer_benefits || []), { text: '', icon_url: null }],
    }));
  const updateBenefit = (index, patch) =>
    setFormData((prev) => {
      const next = [...(prev.second_offer_benefits || [])];
      next[index] = { ...next[index], ...patch };
      return { ...prev, second_offer_benefits: next };
    });
  const removeBenefit = (index) =>
    setFormData((prev) => ({
      ...prev,
      second_offer_benefits: (prev.second_offer_benefits || []).filter((_, i) => i !== index),
    }));

  // Track the latest preview blob so the unmount-only cleanup can revoke it
  // (revoking on every icon_preview change would free a blob still painted).
  const iconPreviewRef = useRef(null);
  iconPreviewRef.current = formData.icon_preview;
  useEffect(() => () => {
    if (iconPreviewRef.current) window.URL.revokeObjectURL(iconPreviewRef.current);
    dispatch(clearMedia());
  }, [dispatch]);

  const handleIconUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Instant local preview, then swap to the uploaded URL once it returns.
    if (formData.icon_preview) window.URL.revokeObjectURL(formData.icon_preview);
    setFormData((prev) => ({ ...prev, icon_preview: window.URL.createObjectURL(file) }));
    dispatch(uploadImage(file))
      .then((response) => {
        const url = response?.data?.image_url;
        // On success, swap the tile to the hosted URL and free the blob.
        setFormData((prev) => {
          if (url && prev.icon_preview) window.URL.revokeObjectURL(prev.icon_preview);
          return {
            ...prev,
            icon_url: url || prev.icon_url,
            icon_preview: url ? null : prev.icon_preview,
          };
        });
        dispatch(clearMedia());
      })
      .catch(() => {
        // Upload failed (errorHandler already alerted) — roll the preview back
        // so the tile reflects reality and the rejection isn't unhandled.
        setFormData((prev) => {
          if (prev.icon_preview) window.URL.revokeObjectURL(prev.icon_preview);
          return { ...prev, icon_preview: null };
        });
      });
  };

  const handleIconRemove = () => {
    if (formData.icon_preview) window.URL.revokeObjectURL(formData.icon_preview);
    setFormData((prev) => ({ ...prev, icon_url: '', icon_preview: null }));
  };

  const buildPayload = () => {
    const { type: contentType, id: contentId } = decodeContent(formData.recommended_content);
    const welcomeTitle = formData.welcome_title.trim();
    const offerTitle = formData.offer_title.trim();
    return {
      ...(isCreate ? { goal_slug: formData.goal_slug.trim() } : {}),
      // The server locks these three for the fallback, so never send them for it.
      ...(isFallback
        ? {}
        : {
            ...(isCreate ? {} : { is_retired: formData.is_retired }),
            show_in_onboarding: formData.show_in_onboarding,
            show_in_settings: formData.show_in_settings,
          }),
      label: formData.label.trim(),
      icon_url: formData.icon_url?.trim() || null,
      create_account_subtitle: formData.create_account_subtitle?.trim() || null,
      // The server rejects an empty title, so an empty one is left out (required only when shown in onboarding).
      ...(welcomeTitle ? { welcome_title: welcomeTitle } : {}),
      welcome_subtitle: formData.welcome_subtitle?.trim() || null,
      welcome_stats: (() => {
        const rows = (formData.welcome_stats || [])
          .map((s) => ({ value: (s.value || '').trim(), label: (s.label || '').trim() }))
          .filter((s) => s.value && s.label);
        return rows.length ? rows : null;
      })(),
      welcome_social_proof: formData.welcome_social_proof?.trim() || null,
      ...(offerTitle ? { offer_title: offerTitle } : {}),
      offer_testimonial: formData.offer_testimonial?.trim() || null,
      offer_testimonial_author: formData.offer_testimonial_author?.trim() || null,
      second_offer_subtitle: formData.second_offer_subtitle?.trim() || null,
      second_offer_benefits: (() => {
        const rows = (formData.second_offer_benefits || [])
          .map((b) => ({ text: (b.text || '').trim(), icon_url: b.icon_url || null }))
          .filter((b) => b.text);
        return rows.length ? rows : null;
      })(),
      // Polymorphic recommendation (both-or-neither): the API resolves the
      // type + id. We also mirror the legacy recommended_session_id — it
      // carries the id only for a Session (null for a Quick Tap) — so an API
      // deployed before this change still reads the Session correctly and
      // never stores a Quick Tap id as a session.
      recommended_content_type: contentType,
      recommended_content_id: contentType ? contentId : null,
      recommended_session_id: contentType === CONTENT_TYPE_SESSION ? contentId : null,
      recommended_session_blurb: formData.recommended_session_blurb?.trim() || null,
      recommended_session_headline: formData.recommended_session_headline?.trim() || null,
      recommended_session_prompt: formData.recommended_session_prompt?.trim() || null,
    };
  };

  const validate = (payload) => {
    if (!payload.label) return 'Label is required.';
    if (payload.label.length > 100) return 'Label must be 100 characters or fewer.';
    if (isCreate) {
      if (!payload.goal_slug) return 'Slug is required.';
      if (payload.goal_slug.length > 100) return 'Slug must be 100 characters or fewer.';
      if (!SLUG_PATTERN.test(payload.goal_slug)) {
        return 'Slug can only use lowercase letters, numbers and underscores.';
      }
    }
    // Mirror the server: a topic shown in onboarding needs both titles.
    const inOnboarding = isFallback || payload.show_in_onboarding;
    if (inOnboarding && !payload.welcome_title) return 'Welcome title is required when the topic is shown in onboarding.';
    if (inOnboarding && !payload.offer_title) return 'Offer title is required when the topic is shown in onboarding.';
    if (payload.recommended_content_type !== null) {
      if (!Number.isInteger(payload.recommended_content_id) || payload.recommended_content_id < 1) {
        return 'The recommended meditation is invalid — please re-pick it.';
      }
    }
    // Mirror the API limits so the admin gets a clear message instead of a 422.
    const benefits = payload.second_offer_benefits;
    if (Array.isArray(benefits)) {
      if (benefits.length > 6) return 'Up to 6 second-offer benefits are allowed.';
      if (benefits.some((b) => (b.text || '').length > 255)) return 'Each benefit must be 255 characters or fewer.';
    }
    if ((payload.create_account_subtitle || '').length > 500) {
      return 'Create Account subtitle must be 500 characters or fewer.';
    }
    if ((payload.second_offer_subtitle || '').length > 500) {
      return 'Second-offer subtitle must be 500 characters or fewer.';
    }
    if ((payload.recommended_session_blurb || '').length > 500) {
      return 'Popup blurb must be 500 characters or fewer.';
    }
    if ((payload.recommended_session_headline || '').length > 100) {
      return 'Popup headline must be 100 characters or fewer.';
    }
    if ((payload.recommended_session_prompt || '').length > 500) {
      return 'Popup prompt must be 500 characters or fewer.';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (isSaving) return; // guard against a double-click firing two PUTs

    const payload = buildPayload();
    const error = validate(payload);
    if (error) {
      Swal.fire({ icon: 'error', text: error });
      return;
    }
    if (confirmSubmit && !(await confirmSubmit())) return;

    setIsSaving(true);
    const result = dispatch(
      isCreate
        ? createGoalContent(payload, () => navigate('/goal-content'))
        : saveGoalContent(selectedSlug, payload, () => navigate('/goal-content')),
    );
    if (result && typeof result.finally === 'function') {
      result.finally(() => setIsSaving(false));
    } else {
      setTimeout(() => setIsSaving(false), 3000);
    }
  };

  // The onboarding-only sections are disabled (not cleared) while the topic is
  // not shown in onboarding. The fallback is always in onboarding.
  const onboardingOff = !isFallback && !formData.show_in_onboarding;

  return (
    <form
      id="goal-content-form"
      onSubmit={handleSubmit}
      onKeyDown={preventEnterFormSubmission}
      autoComplete="off"
      style={{ minWidth: 0, maxWidth: 980 }}
      aria-busy={isSaving}
    >
      <LoadingBackdrop open={isSaving} />
      <FormSection title="Topic settings" num="01">
        {isCreate && (
          <FieldBlock
            label="Slug"
            required
            hint="the permanent id for this topic — lowercase letters, numbers and underscores; it cannot be changed later"
            htmlFor="f-goal-slug"
          >
            <TextInput
              id="f-goal-slug"
              name="goal_slug"
              value={formData.goal_slug}
              onChange={handleSlugChange}
              placeholder="e.g. better_sleep"
              maxLength={100}
            />
          </FieldBlock>
        )}
        <FieldBlock label="Where it appears" noMargin>
          <div className="flex flex-col" style={{ gap: 12 }}>
            <ToggleSwitch
              name="show_in_onboarding"
              checked={isFallback || formData.show_in_onboarding}
              onChange={handleFlagChange('show_in_onboarding')}
              disabled={isFallback}
              label="Show in onboarding"
              hint={isFallback ? 'always on — this is the fallback' : 'people can pick it when they sign up'}
            />
            <ToggleSwitch
              name="show_in_settings"
              checked={!isFallback && formData.show_in_settings}
              onChange={handleFlagChange('show_in_settings')}
              disabled={isFallback}
              label="Show in Settings"
              hint={isFallback ? 'the fallback is never a pickable topic' : 'people can pick it later in Settings'}
            />
            {!isCreate && (
              <ToggleSwitch
                name="is_retired"
                checked={!isFallback && formData.is_retired}
                onChange={handleFlagChange('is_retired')}
                disabled={isFallback}
                label="Retired"
                hint={
                  isFallback
                    ? 'the fallback cannot be retired'
                    : 'hidden from onboarding and Settings; people who already picked it keep getting its sessions until they change their topics'
                }
              />
            )}
          </div>
        </FieldBlock>
      </FormSection>

      <FormSection title="Topic card" num="02">
        <FieldBlock
          label="Label"
          required
          hint="the short name shown on the goal-selection card (e.g. “Better Sleep”)"
          htmlFor="f-goal-label"
        >
          <TextInput
            id="f-goal-label"
            name="label"
            value={formData.label}
            onChange={handleFieldChange}
            placeholder="Type the goal label here…"
            maxLength={100}
          />
        </FieldBlock>
        <UploadTile
          inputId="f-goal-icon"
          label="Image"
          hint="optional — shown on the goal-selection card and the welcome screen"
          value={formData.icon_preview || formData.icon_url}
          accept="image/*"
          onChange={handleIconUpload}
          onRemove={handleIconRemove}
          maxWidth={176}
          previewAspectRatio="1 / 1"
        />
      </FormSection>

      {/* A disabled <fieldset> disables every native control inside it; the
       *  SearchSelects also get `disabled` because their clear control is not one. */}
      <fieldset
        disabled={onboardingOff}
        aria-label="Onboarding content"
        data-testid="onboarding-fields"
        style={{ border: 0, padding: 0, margin: 0, minWidth: 0, opacity: onboardingOff ? 0.5 : 1 }}
      >
      {onboardingOff && (
        <p className="text-text-muted" style={{ fontSize: 12.5, margin: '0 0 12px' }}>
          These fields are only used in onboarding. Turn on “Show in onboarding” to edit them.
        </p>
      )}
      <FormSection title="Create Account screen" num="03">
        <FieldBlock
          label="Subtitle"
          hint="optional — goal-themed subheader on the Create Account screen; falls back to the default copy when blank"
          htmlFor="f-goal-create-account-subtitle"
        >
          <TextInput
            id="f-goal-create-account-subtitle"
            name="create_account_subtitle"
            value={formData.create_account_subtitle || ''}
            onChange={handleFieldChange}
            maxLength={500}
            placeholder="Type the goal-themed subtitle here…"
          />
        </FieldBlock>
      </FormSection>

      <FormSection title="1st free-trial offer" num="04">
        <FieldBlock label="Offer title" required={formData.show_in_onboarding || isFallback} htmlFor="f-goal-offer-title">
          <TextInput
            id="f-goal-offer-title"
            name="offer_title"
            value={formData.offer_title}
            onChange={handleFieldChange}
            placeholder="Type the offer title here…"
          />
        </FieldBlock>
        <FieldBlock
          label="Testimonial"
          hint="optional — quote shown under the offer title"
          htmlFor="f-goal-offer-testimonial"
        >
          <Textarea
            id="f-goal-offer-testimonial"
            name="offer_testimonial"
            value={formData.offer_testimonial || ''}
            onChange={handleFieldChange}
            rows={2}
            maxLength={500}
            placeholder="Type the testimonial quote here…"
          />
        </FieldBlock>
        <FieldBlock
          label="Testimonial author"
          hint="optional — attribution shown beneath the quote"
          htmlFor="f-goal-offer-testimonial-author"
        >
          <TextInput
            id="f-goal-offer-testimonial-author"
            name="offer_testimonial_author"
            value={formData.offer_testimonial_author || ''}
            onChange={handleFieldChange}
            maxLength={255}
            placeholder="Type the author name here…"
          />
        </FieldBlock>
      </FormSection>

      <FormSection title="Welcome screen" num="05">
        <FieldBlock label="Welcome title" required={formData.show_in_onboarding || isFallback} htmlFor="f-goal-welcome-title">
          <TextInput
            id="f-goal-welcome-title"
            name="welcome_title"
            value={formData.welcome_title}
            onChange={handleFieldChange}
            placeholder="Type the Welcome title here…"
          />
        </FieldBlock>
        <FieldBlock label="Welcome subtitle" hint="optional" htmlFor="f-goal-welcome-subtitle">
          <Textarea
            id="f-goal-welcome-subtitle"
            name="welcome_subtitle"
            value={formData.welcome_subtitle || ''}
            onChange={handleFieldChange}
            rows={2}
          />
        </FieldBlock>
        <FieldBlock label="Stat cards" hint="optional — up to 2 (e.g. “21M+” / “Sleep Sessions Completed”). Leave a card blank to omit it.">
          {(formData.welcome_stats || []).map((stat, idx) => (
            <FieldRow key={idx}>
              <FieldBlock label={idx === 0 ? 'Value' : undefined}>
                <TextInput
                  value={stat.value}
                  onChange={(e) => updateStat(idx, 'value', e.target.value)}
                  placeholder="Type a value…"
                  maxLength={32}
                />
              </FieldBlock>
              <FieldBlock label={idx === 0 ? 'Label' : undefined}>
                <TextInput
                  value={stat.label}
                  onChange={(e) => updateStat(idx, 'label', e.target.value)}
                  placeholder="Type a label…"
                  maxLength={64}
                />
              </FieldBlock>
            </FieldRow>
          ))}
        </FieldBlock>
        <FieldBlock
          label="Social-proof callout"
          hint="optional — e.g. “Our most popular session, ‘Quiet My Racing Mind,’ has over 1 million completions.”"
          htmlFor="f-goal-social-proof"
        >
          <Textarea
            id="f-goal-social-proof"
            name="welcome_social_proof"
            value={formData.welcome_social_proof || ''}
            onChange={handleFieldChange}
            rows={3}
            maxLength={500}
          />
        </FieldBlock>
      </FormSection>

      <FormSection title="2nd free-trial offer" num="06">
        <FieldBlock label="Subtitle" hint="optional" htmlFor="f-goal-second-offer-subtitle">
          <Textarea
            id="f-goal-second-offer-subtitle"
            name="second_offer_subtitle"
            value={formData.second_offer_subtitle || ''}
            onChange={handleFieldChange}
            rows={2}
            maxLength={500}
            placeholder="Type the goal-themed subtitle here…"
          />
        </FieldBlock>
        <FieldBlock
          label="Benefits"
          hint="optional — up to 6 (the screen shows four); each row is a line of copy + an icon from Media Resources"
        >
          <div className="flex flex-col" style={{ gap: 10 }}>
            {(formData.second_offer_benefits || []).map((b, i) => (
              <div key={i} className="flex" style={{ gap: 8, alignItems: 'center' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <TextInput
                    id={`f-goal-benefit-text-${i}`}
                    value={b.text || ''}
                    onChange={(e) => updateBenefit(i, { text: e.target.value })}
                    maxLength={255}
                    placeholder="Type a benefit line…"
                  />
                </div>
                <div style={{ width: 230, flexShrink: 0 }}>
                  <SearchSelect
                    id={`f-goal-benefit-icon-${i}`}
                    value={b.icon_url || null}
                    onChange={(url) => updateBenefit(i, { icon_url: url || null })}
                    options={mediaOptions}
                    placeholder={Array.isArray(mediaResources) ? 'Pick an icon…' : 'Loading icons…'}
                    searchPlaceholder="Search Media Resources"
                    clearable
                    disabled={onboardingOff}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => removeBenefit(i)}
                  aria-label={`Remove benefit ${i + 1}`}
                  className="font-condensed font-bold uppercase cursor-pointer"
                  style={{
                    flexShrink: 0,
                    height: 32,
                    padding: '0 10px',
                    border: '1px solid transparent',
                    borderRadius: 4,
                    background: 'transparent',
                    color: '#C4715B',
                    fontSize: 11,
                    letterSpacing: '0.06em',
                  }}
                >
                  Remove
                </button>
              </div>
            ))}
            {(formData.second_offer_benefits || []).length < 6 && (
              <button
                type="button"
                onClick={addBenefit}
                className="font-condensed font-bold uppercase cursor-pointer"
                style={{
                  alignSelf: 'flex-start',
                  height: 32,
                  padding: '0 12px',
                  border: '1px solid rgb(var(--color-sand))',
                  borderRadius: 4,
                  background: 'rgb(var(--color-surface))',
                  color: 'rgb(var(--color-navy))',
                  fontSize: 11,
                  letterSpacing: '0.06em',
                }}
              >
                + Add benefit
              </button>
            )}
          </div>
        </FieldBlock>
      </FormSection>

      <FormSection title="Recommendation" num="07">
        <FieldBlock
          label="Recommended meditation"
          hint="optional — the Session or Quick Tap surfaced for this goal"
        >
          <SearchSelect
            id="f-goal-recommended-content"
            value={formData.recommended_content || null}
            onChange={(val) =>
              setFormData((prev) => ({ ...prev, recommended_content: val || '' }))
            }
            options={recommendedOptions}
            placeholder={
              Array.isArray(allSessions) && Array.isArray(allQuickTaps)
                ? 'Search sessions & Quick Taps…'
                : 'Loading…'
            }
            searchPlaceholder="Search by name"
            clearable
            disabled={onboardingOff}
          />
        </FieldBlock>
        <FieldBlock
          label="Popup headline"
          hint="optional — goal-themed headline for the recommended-meditation popup"
          htmlFor="f-goal-recommended-session-headline"
        >
          <TextInput
            id="f-goal-recommended-session-headline"
            name="recommended_session_headline"
            value={formData.recommended_session_headline || ''}
            onChange={handleFieldChange}
            maxLength={100}
            placeholder="Type the popup headline here…"
          />
        </FieldBlock>
        <FieldBlock
          label="Popup blurb"
          hint="optional — goal-themed body copy for the recommended-meditation popup"
          htmlFor="f-goal-recommended-session-blurb"
        >
          <Textarea
            id="f-goal-recommended-session-blurb"
            name="recommended_session_blurb"
            value={formData.recommended_session_blurb || ''}
            onChange={handleFieldChange}
            rows={3}
            maxLength={500}
            placeholder="Type the popup blurb here…"
          />
        </FieldBlock>
        <FieldBlock
          label="Popup prompt"
          hint="optional — goal-themed call-to-action copy for the recommended-meditation popup"
          htmlFor="f-goal-recommended-session-prompt"
        >
          <Textarea
            id="f-goal-recommended-session-prompt"
            name="recommended_session_prompt"
            value={formData.recommended_session_prompt || ''}
            onChange={handleFieldChange}
            rows={3}
            maxLength={500}
            placeholder="Type the popup prompt here…"
          />
        </FieldBlock>
      </FormSection>
      </fieldset>
    </form>
  );
}
